import { cn } from '@/lib/utils';
import { STATUS_LABELS } from '@/lib/company-qualification';
import { EVIDENCE_LABELS } from '@/lib/company-restitution';
import type { QualificationItem, QualificationStatus } from '@shared/types/company-qualification';
import { evidenceStatus } from '@shared/types/company-restitution';

const TONES: Record<QualificationStatus | 'none', string> = {
  validated: 'bg-emerald-50 text-emerald-800',
  observed: 'bg-sky-50 text-sky-800',
  hypothesis: 'bg-amber-50 text-amber-800',
  gap: 'bg-stone-100 text-stone-600',
  none: 'bg-stone-100 text-stone-500',
};

export type Lines = ReadonlyMap<string, QualificationItem>;

/** The status of an element, computed from the weakest line it cites; a click shows those lines. */
export const EvidenceButton = ({ ids, lines, open, onToggle }: { ids: readonly string[]; lines: Lines; open: boolean; onToggle: () => void }) => {
  const status = evidenceStatus(lines, ids) ?? 'none';
  const count = ids.filter((id) => lines.has(id)).length;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={count === 0}
      aria-expanded={open}
      title={count ? `${count} constat${count > 1 ? 's' : ''} : voir les sources` : 'Aucun constat ne soutient cet élément'}
      className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] leading-5', TONES[status], count > 0 && 'hover:ring-1 hover:ring-current')}
    >
      {EVIDENCE_LABELS[status]}
    </button>
  );
};

/** The lines of the sheet behind an element, with their status and source. */
export const EvidenceLines = ({ ids, lines, className }: { ids: readonly string[]; lines: Lines; className?: string }) => (
  <ul className={cn('space-y-1.5 rounded-lg bg-domino-muted px-3 py-2 text-xs leading-5 text-domino-inkSoft', className)}>
    {ids.flatMap((id) => {
      const line = lines.get(id);
      if (!line) return [];
      const ref = line.source?.ref && /^https?:\/\//.test(line.source.ref) ? line.source.ref : null;
      return [(
        <li key={id}>
          <span className="text-domino-ink">{line.text}</span>
          <span> · {STATUS_LABELS[line.status]}</span>
          {line.source && <> · {ref ? <a href={ref} target="_blank" rel="noreferrer" className="underline">{line.source.label}</a> : line.source.label}</>}
        </li>
      )];
    })}
  </ul>
);
